import React from 'react';
import styled from 'styled-components';
import { Link, LinkProps } from 'react-router-dom';
import { clickHandler } from './Buttons';

type AnchorComponentTypes = {
    children: React.ReactNode;
    href: string;
    target?: '_blank' | '_self';
    rel?:
        | 'noopener noreferrer nofollow'
        | 'noopener noreferrer'
        | 'nofollow'
        | 'noreferrer'
        | 'alternate'
        | 'external'
        | 'next'
        | 'prev';
    className?: string;
    onClick?: Function;
    desc?: string;
};

export type AnchorRef = HTMLAnchorElement;

export const AnchorComponent = React.forwardRef<AnchorRef, AnchorComponentTypes>(
    (
        { onClick, children, target = '_blank', rel = 'noopener noreferrer', desc, href, ...props },
        ref
    ) => (
        <a
            onClick={clickHandler(onClick)}
            href={href}
            target={target}
            rel={rel}
            {...props}
            ref={ref}
            title={desc}
        >
            {children}
        </a>
    )
);

export type LinkComponentTypes = LinkProps &
    React.RefAttributes<HTMLAnchorElement> & {
        onClick?: Function;
        children: React.ReactNode;
        desc?: string;
        disabled?: boolean;
        to: string;
    };

export type LinkComponentRef = HTMLAnchorElement;

export const LinkComponent = React.forwardRef<LinkComponentRef, LinkComponentTypes>(
    ({ onClick, children, desc, disabled = false, to, ...props }, ref) =>
        typeof to === 'string' ? (
            <a
                onClick={disabled ? () => {} : clickHandler(onClick)}
                ref={ref}
                title={desc}
                href={disabled ? '.#' : to}
                {...props}
            >
                {children}
            </a>
        ) : (
            <Link
                onClick={disabled ? () => {} : clickHandler(onClick)}
                ref={ref}
                title={desc}
                to={disabled ? '#' : to}
                {...props}
            >
                {children}
            </Link>
        )
);

type AnchorButtonTypes = {
    children: React.ReactNode;
    onClick?: Function;
    className?: string;
    desc?: string;
};
export type AnchorButtonRef = HTMLButtonElement;

const AnchorButtonComponent = React.forwardRef<AnchorButtonRef, AnchorButtonTypes>(
    ({ children, onClick, desc, ...props }, ref) => (
        <button onClick={clickHandler(onClick)} type="button" {...props} ref={ref} title={desc}>
            {children}
        </button>
    )
);

type AnchorStyleTypes = {
    decoration?: 'underline' | 'none';
    color?: 'blue' | undefined;
};

export const Anchor = styled(AnchorComponent)<AnchorStyleTypes>`
    text-decoration: ${(props: any) => props.decoration || 'none;'};
    color: ${(props: any) => props.color || 'var(--cademy-blue);'};
    cursor: pointer;
    font-weight: inherit;
    font-size: inherit;
    background: none;
    border: none;
    padding: 0px;

    &:hover {
        text-decoration: none;
    }

    &:focus {
        outline: none;
    }
`;

// A Button that looks like a link
export const AnchorButton = Anchor.withComponent(AnchorButtonComponent);
